import { useState, useEffect, useCallback } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { debounce } from "lodash";
import { IUser } from "authentication/domain/IUser";
import { authServices } from "authentication/services/auth.service";
import UserItem from "./UserItem";
import { IUserGroup } from "authentication/domain/IUserGroup";
import NewUserModal from "./NewUserModal";
import DeleteUserModal from "./DeleteUserModal";
import UpdateUserPasswordModal from "./UpdatePasswordModal";
import UploadPhotoModal from "./UploadPhotoModal";
import { ImageUploadType } from "Dashboard/domain/ImageUploadType";
import UserGroupsModal from "./UserGroupsModal";
import { hrService } from "Dashboard/scenes/HumanResources/services/hr.services";
import { IEmployee } from "Dashboard/scenes/HumanResources/domain/IEmployee";
import { IEmployee as IUserEmployee } from "authentication/domain/IEmployee";

interface IProps {
  setClickOutsideActive: Function;
  currentUser: IUser;
}

const UsersList = (props: IProps) => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [indexOfSelectedUser, setIndexOfSelectedUser] = useState<number | null>(null);
  const [availableGroups, setAvailableGroups] = useState<IUserGroup[]>([]);

  const [isOpen1, setIsOpen1] = useState(false);
  const [isOpen2, setIsOpen2] = useState(false);
  const [isOpen3, setIsOpen3] = useState(false);
  const [isOpen4, setIsOpen4] = useState(false);
  const [isOpen5, setIsOpen5] = useState(false);

  const { setClickOutsideActive, currentUser } = props;

  const delayedQueryUser = useCallback(
    debounce(async (data, username) => {
      try {
        authServices.updateUser(data, username);
      } catch (err) {
        console.log(err);
      }
    }, 1000),
    []
  );

  const openNewUserModal = () => {
    setIsOpen1(true);
    setClickOutsideActive(false);
  };

  const openDeleteUserModal = () => {
    setIsOpen2(true);
    setClickOutsideActive(false);
  };

  const openUserGroupsModal = () => {
    setIsOpen3(true);
    setClickOutsideActive(false);
  };

  const openChangeUserPasswordModal = () => {
    setIsOpen4(true);
    setClickOutsideActive(false);
  };

  const openUploadImageModal = () => {
    setIsOpen5(true);
    setClickOutsideActive(false);
  };

  const getUsers = async () => {
    try {
      const users = await authServices.getUsers();
      setUsers(users);
    } catch (error) {
      console.log(error);
    }
  };

  const chooseSelectedUser = (selectedUser: IUser) => {
    setIndexOfSelectedUser(users
      ? users.map(user => user.username).indexOf(selectedUser.username)
      : null);
  }

  useEffect(() => {
    if (indexOfSelectedUser !== null && users[indexOfSelectedUser]) {
      setSelectedUser(users[indexOfSelectedUser]);
    } else {
      setSelectedUser(null);
    }
  }, [users, indexOfSelectedUser]);

  const getEmployees = async () => {
    try {
      const employees = await hrService.getAllEmployees();
      setEmployees(employees);
    } catch (err) {
      console.error(err);
    }
  };

  const updateUser = (username: string, name: string, newName?: string, employeeId?: number) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUsers(users.map((user) =>
        user.username === username
          ? name === "group"
            ? !event.target.checked
              ? { ...user, userGroups: removeGroup(user.userGroups, event.target.value) }
              : { ...user, userGroups: [...user.userGroups, getGroup(event.target.value)] }
            : (name === "name"
              ? changeName(user, name, newName)
              : (name === "employee"
                ? {
                  ...user, employee: employeeId && employeeId > 0
                    ? getEmployee(user.username, employeeId) : undefined
                }
                : clearNameField(user)
              ))
          : { ...user }));
    };

  const updateUserFromObject = (updatedUser: IUser) => {
    setUsers(users.map((user) => user.username === updatedUser.username ? updatedUser : user));
  }

  useEffect(() => {
    async function getRoles() {
      try {
        const availableGroups = await authServices.getRoles();
        setAvailableGroups(availableGroups);
      } catch (error) {
        console.log(error);
      }
    }
    getRoles();
    getUsers();
    getEmployees();
  }, []);

  function getGroup(groupName: string): IUserGroup {
    const group = availableGroups.find((g) => g.name === groupName);
    if (group) {
      return group;
    }
    return availableGroups[0];
  };

  function removeGroup(userGroups: IUserGroup[], groupName: string): IUserGroup[] {
    const groups = userGroups.filter(el => el.name !== groupName);
    if (groups) {
      return groups;
    }
    return [];
  };

  function changeName(user: IUser, name: string, newName: string | undefined): IUser {
    if (newName !== undefined) {
      let splitted = newName.split(" ");
      let firstN = splitted[0];
      splitted.shift();
      splitted.join(",");
      let fullName = firstN.concat(" ", splitted.toString());
      delayedQueryUser(
        { name: fullName },
        user.username
      );
    }
    return { ...user, [name]: newName };
  };

  function getEmployee(username: string, employeeId: number): IUserEmployee {
    const employee = employees.find((empl) => empl.employeeId === employeeId);
    let userEmployee: IUserEmployee = { employeeId: 0, name: "", role: "" };
    if (employee) {
      userEmployee =
      {
        employeeId: employee?.employeeId,
        name: employee?.employeeName,
        role: employee?.employeeRole.roleName
      }
    }
    delayedQueryUser({ employeeId }, username);
    return userEmployee;
  };

  function clearNameField(user: IUser): IUser {
    delayedQueryUser({ name: null }, user.username);
    return { ...user, name: null, employee: undefined };
  };

  return (
    <div className="settings-tooltip-section">
      <span className="settings-tooltip-header mt-10">
        Unos i podešavanje korisnika programa
      </span>
      <span className="plus-button" onClick={openNewUserModal}>
        {/* <FontAwesomeIcon icon={faPlusCircle} /> */}
        <AddCircleOutlineIcon className="" />
      </span>
      <div className="settings-tooltip-users-list">
        {users.map((user) => (
          <UserItem
            user={user}
            key={user.username}
            currentUser={currentUser}
            employees={employees}
            openDeleteUserModal={openDeleteUserModal}
            openChangeUserPasswordModal={openChangeUserPasswordModal}
            openUserGroupsModal={openUserGroupsModal}
            openUploadImageModal={openUploadImageModal}
            setSelectedUser={chooseSelectedUser}
            updateFieldChanged={updateUser}
            roles={availableGroups}
          />
        ))}
      </div>
      <NewUserModal
        isOpen={isOpen1}
        refresh={getUsers}
        setIsOpen={setIsOpen1}
        setClickOutsideActive={setClickOutsideActive}
      />
      <DeleteUserModal
        isOpen={isOpen2}
        refresh={getUsers}
        setIsOpen={setIsOpen2}
        selectedUser={selectedUser}
        setClickOutsideActive={setClickOutsideActive}
      />
      <UserGroupsModal
        isOpen={isOpen3}
        setIsOpen={setIsOpen3}
        setClickOutsideActive={setClickOutsideActive}
        user={selectedUser}
        availableGroups={availableGroups}
        updateUser={updateUserFromObject}
      />
      <UpdateUserPasswordModal
        isOpen={isOpen4}
        setIsOpen={setIsOpen4}
        user={selectedUser}
        currentUser={currentUser}
        setClickOutsideActive={setClickOutsideActive}
      />
      <UploadPhotoModal
        uploadType={ImageUploadType.SPECIFIC_LOGO}
        isOpen={isOpen5}
        refresh={() => { }}
        setIsOpen={setIsOpen5}
        user={selectedUser}
        setClickOutsideActive={setClickOutsideActive}
        aspect={1 / 1}
      />
    </div>
  );
};

export default UsersList;
